import { i18n } from '../services/i18n.js';
import { dbSim } from '../services/dbSim.js';
import { realtimeSim } from '../services/realtimeSim.js';
import { escapeHTML } from '../services/sanitize.js';
import { icon } from '../services/icons.js';

export const LobbyView = async (container, params) => {
  container.innerHTML = `<div class="container" style="padding:40px; text-align:center; color:var(--color-text-secondary);">${i18n.t('lobby_loading')}</div>`;

  // Auth Guard
  if (!localStorage.getItem("h150_auth_user")) {
    window.location.hash = '#/login';
    return;
  }

  const session = await dbSim.getSession(params.id);
  if (!session) {
    window.showToast(i18n.t('session_not_found'));
    window.location.hash = '#/dashboard';
    return;
  }
  if (session.status !== 'lobby') {
    window.showToast(session.status === 'finished' ? i18n.t('game_finished') : i18n.t('session_started'));
    window.location.hash = session.status === 'finished' ? `#/reports/${session.id}` : '#/dashboard';
    return;
  }

  const game = await dbSim.getSessionGame(session.id);
  
  // Set up scannable Join URL
  const joinUrl = `${window.location.origin}${window.location.pathname}#/join?pin=${session.pin}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&color=0f172a&bgcolor=ffffff&data=${encodeURIComponent(joinUrl)}`;

  let participants = await dbSim.getParticipants(session.id);
  const isPreview = session.mode === 'preview';
  const minimumPlayers = 5;
  const canStart = () => isPreview || participants.length >= minimumPlayers;
  const lobbyStatus = () => {
    if (isPreview) return i18n.t('preview_ready');
    if (participants.length >= minimumPlayers) return i18n.t('everyone_ready');
    return i18n.t('need_more_players').replace('{count}', minimumPlayers - participants.length);
  };

  const syncParticipantUI = () => {
    const countEl = container.querySelector('#partCountText');
    const feedEl = container.querySelector('#lobbyFeed');
    const startBtn = container.querySelector('#startGameBtn');
    const statusEl = container.querySelector('#lobbyStatusText');
    if (countEl) countEl.innerText = participants.length;
    if (statusEl) statusEl.innerText = lobbyStatus();
    if (startBtn) startBtn.disabled = !canStart();
    if (feedEl) {
      feedEl.innerHTML = participants.map(participant => `
        <div class="lobby-bubble" style="padding:10px 20px; background:var(--color-surface); border:1px solid var(--color-border); border-radius:30px; font-weight:600; font-size:15px; animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;">
          ${icon('smile')} ${escapeHTML(participant.nickname)}
        </div>
      `).join('');
    }
  };

  const refreshParticipants = async () => {
    try {
      const latest = await dbSim.getParticipants(session.id);
      const changed = latest.length !== participants.length || latest.some((item, index) => item.id !== participants[index]?.id);
      if (changed) {
        participants = latest;
        syncParticipantUI();
      }
    } catch (error) {
      console.warn('[Lobby] Participant sync failed', error.message);
    }
  };

  const render = () => {
    container.innerHTML = `
      <div class="host-screen" style="flex:1; display:flex; flex-direction:column; background: var(--color-bg); padding:40px 24px; justify-content:space-between; min-height:100vh;">
        
        <!-- Header -->
        <div class="host-header" style="display:flex; justify-content:space-between; align-items:center;">
          <a href="#/dashboard" class="btn btn-secondary" style="padding:10px 20px;">
            ${icon('arrowLeft')} ${i18n.t('back_to_dashboard')}
          </a>
          <h2 style="font-size:20px; font-weight:800; color:var(--color-text-secondary);">${escapeHTML(game.title)} ${isPreview ? `<span class="badge badge-green">${i18n.t('preview_mode')}</span>` : ''}</h2>
          <button id="startGameBtn" class="btn btn-primary btn-lg" style="box-shadow: var(--shadow-glow); min-width:160px;" ${canStart() ? '' : 'disabled'}>
            ${icon('bolt')} ${i18n.t(isPreview ? 'start_preview' : 'lobby_start')}
          </button>
        </div>

        <!-- Center: PIN & QR -->
        <div class="lobby-flex" style="margin:40px 0;">
          
          <!-- QR Card -->
          <div class="card" style="padding:24px; text-align:center; background: var(--color-surface); width:260px; display:flex; flex-direction:column; align-items:center; justify-content:center;">
            <img src="${qrUrl}" alt="Scan QR to Join" style="width:200px; height:200px; border-radius: var(--radius-md); border:4px solid var(--color-border); background:#fff;" />
            <p style="font-size:13px; color:var(--color-text-secondary); margin-top:16px; font-weight:600;">
              ${i18n.t('lobby_qr')}
            </p>
          </div>

          <!-- PIN Card -->
          <div style="text-align:center;">
            ${game.brand?.name ? `
              <div class="lobby-brand">
                <strong>${escapeHTML(game.brand.name)}</strong>
                ${game.brand.tagline ? `<span>${escapeHTML(game.brand.tagline)}</span>` : ''}
              </div>
            ` : ''}
            <div style="font-size:14px; font-weight:800; color:var(--color-text-secondary); letter-spacing:2px; margin-bottom:8px;">
              ${i18n.t('lobby_pin')}
            </div>
            <div id="lobbyPinDisplay" class="host-pin" style="font-size:96px; font-weight:800; color:var(--color-primary); letter-spacing:6px; line-height:1; text-shadow: var(--shadow-glow);">
              ${session.pin}
            </div>
            <div style="margin-top:16px; font-size:14px; color:var(--color-text-secondary);">
              ${i18n.t('or_visit')} <span style="color:var(--color-text-primary); font-weight:700;">heart150.com/join</span>
            </div>
          </div>
        </div>

        <!-- Footer: Participants joined -->
        <div class="card" style="flex:1; max-height:300px; display:flex; flex-direction:column; background:var(--color-surface-subtle);">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; border-bottom:1px solid var(--color-border); padding-bottom:8px;">
            <span style="font-weight:700; font-size:15px; color:var(--color-text-secondary);">
              ${i18n.t('lobby_count')}: <strong id="partCountText" style="color:var(--color-text-primary); font-size:18px;">${participants.length}</strong>${isPreview ? '' : ` / ${minimumPlayers}`}
            </span>
            <span style="font-size:12px; color:var(--color-text-muted);" id="lobbyStatusText">
              ${lobbyStatus()}
            </span>
          </div>

          <!-- Joining Feed -->
          <div id="lobbyFeed" style="flex:1; overflow-y:auto; display:flex; flex-wrap:wrap; gap:12px; align-content:flex-start; padding-bottom:10px;">
            ${participants.map(p => `
              <div class="lobby-bubble" style="padding:10px 20px; background:var(--color-surface); border:1px solid var(--color-border); border-radius:30px; font-weight:600; font-size:15px; animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;">
                ${icon('smile')} ${escapeHTML(p.nickname)}
              </div>
            `).join('')}
          </div>
        </div>

      </div>
    `;

    // Inject CSS for dynamic animations if not already present
    if (!document.getElementById('lobby-styles')) {
      const style = document.createElement('style');
      style.id = 'lobby-styles';
      style.innerHTML = `
        @keyframes popIn {
          0% { transform: scale(0); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .lobby-bubble {
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
      `;
      document.head.appendChild(style);
    }

    // Bind Start Event
    container.querySelector('#startGameBtn').addEventListener('click', async () => {
      if (!canStart()) return window.showToast(i18n.t('live_minimum_help'));
      // Transition Session to active state
      session.status = "active";
      session.currentQuestionIndex = 0;
      session.currentQuestionStartedAt = new Date().toISOString();
      try {
        await dbSim.saveSession(session);
      } catch (error) {
        session.status = 'lobby';
        window.showToast(error.message);
        return;
      }

      // Publish Realtime Start Game
      realtimeSim.publish("START_GAME", {
        sessionId: session.id,
        gameId: game.id,
        currentQuestionIndex: 0,
        currentQuestionStartedAt: session.currentQuestionStartedAt
      });

      // Redirect Host to presenter screen
      window.location.hash = `#/sessions/${session.id}/present`;
    });
  };

  // Connect to the Cloudflare Durable Object room.
  realtimeSim.connect(session.pin, async (type, payload) => {
    if (type === "PLAYER_JOINED") {
      // Re-fetch participants from Cloudflare D1 through the Worker API.
      await refreshParticipants();
      window.showToast(`${payload.nickname} ${i18n.t('player_joined')}!`);
    }
  });

  render();
  const participantPoll = setInterval(refreshParticipants, 1500);

  // Handle cleanup when leaving lobby
  const originalHashChange = window.onhashchange;
  window.addEventListener('hashchange', function cleanup() {
    clearInterval(participantPoll);
    realtimeSim.disconnect();
    window.removeEventListener('hashchange', cleanup);
  });
};
